/**
 * Evidence Store
 * 
 * Zustand store for evidence management with CRUD and attribute binding.
 * Part of Workstream 2: Evidence System
 * 
 * Supabase is the primary data store (no localStorage persist).
 */

import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import { useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import type {
  Evidence,
  AttributeEvidenceBinding,
  EvidenceQualityMetrics,
  CreateEvidenceInput,
  UpdateEvidenceInput,
  EvidenceFilters,
  EvidenceStatistics,
  EvidenceType,
  EvidenceReliability,
} from '../../types/prioritization/evidence';
import { calculateEvidenceQualityScore } from '../../types/prioritization/evidence';
import {
  getSafeSupabaseClient,
  getCurrentWorkspaceId,
  isSupabaseAvailable,
} from '../../lib/supabaseSync';
import { dispatchStoreError } from '../../hooks/prioritization/useStoreErrorHandler';
import { useUserStore } from './userStore';
import { recordItemFieldChange } from './itemsStore';
import { isUuid } from '../../lib/isUuid';

const TABLE_NAME = 'evidence';
const BINDINGS_TABLE = 'evidence_attribute_bindings';

interface EvidenceState {
  // Data
  evidence: Record<string, Evidence>;
  bindings: Record<string, AttributeEvidenceBinding>;
  
  // Loading state
  isLoading: boolean;
  error: string | null;
  
  // Supabase sync
  fetchFromSupabase: () => Promise<void>;
  
  // Actions
  addEvidence: (input: CreateEvidenceInput) => Evidence;
  updateEvidence: (id: string, updates: UpdateEvidenceInput) => void;
  deleteEvidence: (id: string) => void;
  
  // Binding actions
  bindToAttribute: (evidenceId: string, attributeKey: string, valueSupported: unknown, strength: 'strong' | 'moderate' | 'suggestive') => AttributeEvidenceBinding;
  unbindFromAttribute: (bindingId: string) => void;
  
  // Verification
  verifyEvidence: (id: string, verifierId: string, note?: string) => void;
  
  // Bulk operations
  bulkDeleteEvidence: (ids: string[]) => void;
  deleteEvidenceForItem: (itemId: string) => void;
  
  // Selectors
  getEvidenceById: (id: string) => Evidence | undefined;
  getEvidenceForItem: (itemId: string) => Evidence[];
  getEvidenceForAttribute: (itemId: string, attributeKey: string) => Evidence[];
  getEvidenceQuality: (itemId: string) => EvidenceQualityMetrics;
  getEvidenceByFilters: (filters: EvidenceFilters) => Evidence[];
  getEvidenceStatistics: () => EvidenceStatistics;
  
  // Utility
  reset: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
}

const initialState = {
  evidence: {},
  bindings: {},
  isLoading: false,
  error: null,
};

function dbToBinding(row: Record<string, unknown>): AttributeEvidenceBinding {
  return {
    id: row.id as string,
    evidenceId: row.evidence_id as string,
    attributeKey: row.attribute_key as string,
    valueSupported: row.value_supported ?? null,
    strength: (row.strength as AttributeEvidenceBinding['strength']) ?? 'moderate',
    notes: (row.notes as string | null) ?? undefined,
    createdAt: row.created_at ? new Date(row.created_at as string) : new Date(),
    createdBy: (row.created_by as string | null) ?? '',
  };
}

function bindingToDb(b: AttributeEvidenceBinding): Record<string, unknown> {
  return {
    id: b.id,
    evidence_id: b.evidenceId,
    attribute_key: b.attributeKey,
    value_supported: b.valueSupported ?? null,
    strength: b.strength,
    notes: b.notes ?? null,
    created_at: b.createdAt.toISOString(),
    created_by: isUuid(b.createdBy) ? b.createdBy : null,
  };
}

/** Make the stored links for one piece of evidence exactly `bindings` (replace, not merge). */
async function saveBindings(evidenceId: string, bindings: AttributeEvidenceBinding[]): Promise<void> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) return;
  const keep = bindings.map((b) => b.id);
  let del = supabase.from(BINDINGS_TABLE).delete().eq('evidence_id', evidenceId);
  if (keep.length) del = del.not('id', 'in', `(${keep.join(',')})`);
  const { error: delError } = await del;
  if (delError) {
    dispatchStoreError('Evidence', 'update the values it bears on', delError.message);
    return;
  }
  if (bindings.length === 0) return;
  const { error } = await supabase.from(BINDINGS_TABLE).upsert(bindings.map(bindingToDb));
  if (error) dispatchStoreError('Evidence', 'save the values it bears on', error.message);
}

// Helper to convert DB row to Evidence
function dbToEvidence(row: Record<string, unknown>): Evidence {
  return {
    id: row.id as string,
    itemId: row.item_id as string,
    type: row.type as EvidenceType,
    reliability: row.reliability as EvidenceReliability,
    accessLevel: (row.access_level as 'public' | 'internal' | 'restricted') || 'internal',
    source: row.source as string,
    sourceType: row.source_type as string | undefined,
    link: row.link as string | undefined,
    supportsClaim: row.supports_claim as string | undefined,
    summary: row.summary as string,
    rawContent: row.raw_content as string | undefined,
    // Links to value dimensions live in evidence_attribute_bindings (embedded on fetch).
    attributeBindings: Array.isArray(row.evidence_attribute_bindings)
      ? (row.evidence_attribute_bindings as Record<string, unknown>[]).map(dbToBinding)
      : [],
    addedBy: row.added_by as string,
    addedAt: new Date(row.added_at as string),
    verifiedBy: row.verified_by as string | undefined,
    verifiedAt: row.verified_at ? new Date(row.verified_at as string) : undefined,
    verificationNote: row.verification_note as string | undefined,
    expiresAt: row.expires_at ? new Date(row.expires_at as string) : undefined,
    isExpired: row.is_expired as boolean || false,
    tags: row.tags as string[] | undefined,
  };
}

// Helper to convert Evidence to DB format
function evidenceToDb(evidence: Evidence): Record<string, unknown> {
  return {
    id: evidence.id,
    item_id: evidence.itemId,
    type: evidence.type,
    reliability: evidence.reliability,
    access_level: evidence.accessLevel,
    source: evidence.source,
    source_type: evidence.sourceType,
    link: evidence.link,
    supports_claim: evidence.supportsClaim,
    summary: evidence.summary,
    raw_content: evidence.rawContent,
    // attribute_bindings is not a column — links are saved to evidence_attribute_bindings.
    added_by: isUuid(evidence.addedBy) ? evidence.addedBy : null,
    added_at: evidence.addedAt.toISOString(),
    verified_by: evidence.verifiedBy,
    verified_at: evidence.verifiedAt?.toISOString(),
    verification_note: evidence.verificationNote,
    expires_at: evidence.expiresAt?.toISOString(),
    is_expired: evidence.isExpired,
    tags: evidence.tags,
    workspace_id: getCurrentWorkspaceId(),
  };
}

export const useEvidenceStore = create<EvidenceState>()(
  (set, get) => ({
    ...initialState,
    
    // Supabase sync
    fetchFromSupabase: async () => {
      if (!isSupabaseAvailable()) {
        console.log('[EvidenceStore] Supabase not available');
        return;
      }
      
      set({ isLoading: true, error: null });
      
      try {
        const supabase = getSafeSupabaseClient();
        if (!supabase) return;
        
        const { data, error } = await supabase
          .from(TABLE_NAME)
          .select('*, evidence_attribute_bindings(*)')
          .order('added_at', { ascending: false });
        
        if (error) {
          console.error('[EvidenceStore] Fetch error:', error);
          set({ isLoading: false, error: error.message });
          return;
        }
        
        if (data) {
          const evidenceMap: Record<string, Evidence> = {};
          for (const row of data) {
            const evidence = dbToEvidence(row as Record<string, unknown>);
            evidenceMap[evidence.id] = evidence;
          }
          set({ evidence: evidenceMap, isLoading: false, error: null });
          console.log(`[EvidenceStore] Loaded ${data.length} evidence items from Supabase`);
        }
      } catch (err) {
        console.error('[EvidenceStore] Fetch exception:', err);
        set({ isLoading: false, error: err instanceof Error ? err.message : 'Unknown error' });
      }
    },
    
    // Add new evidence
    addEvidence: (input: CreateEvidenceInput): Evidence => {
        const id = uuidv4();
        const now = new Date();
        
        const evidence: Evidence = {
          id,
          itemId: input.itemId,
          type: input.type,
          reliability: input.reliability,
          accessLevel: input.accessLevel || 'internal',
          source: input.source,
          sourceType: input.sourceType,
          link: input.link,
          supportsClaim: input.supportsClaim,
          summary: input.summary,
          rawContent: input.rawContent,
          attributeBindings: (input.attributeBindings ?? []).map((b) => ({ ...b, evidenceId: id })),
          addedBy: useUserStore.getState().userId ?? '',
          addedAt: now,
          expiresAt: input.expiresAt,
          isExpired: input.expiresAt ? input.expiresAt < now : false,
          tags: input.tags,
        };
        
        set(state => ({
          evidence: { ...state.evidence, [id]: evidence },
        }));
        
        // Sync to Supabase
        if (isSupabaseAvailable()) {
          const supabase = getSafeSupabaseClient();
          if (supabase) {
            supabase.from(TABLE_NAME).insert(evidenceToDb(evidence)).then(({ error }) => {
              if (error) {
                console.error('[EvidenceStore] Insert error:', error);
                dispatchStoreError('Evidence', 'add evidence', error.message);
                return;
              }
              recordItemFieldChange(evidence.itemId, 'evidence', null, evidence.source);
              if (evidence.attributeBindings.length) void saveBindings(evidence.id, evidence.attributeBindings);
            });
          }
        }
        
        return evidence;
      },
      
      // Update evidence
      updateEvidence: (id: string, updates: UpdateEvidenceInput) => {
        const existing = get().evidence[id];
        if (!existing) return;
        
        const updated: Evidence = {
          ...existing,
          ...updates,
          isExpired: updates.expiresAt ? updates.expiresAt < new Date() : existing.isExpired,
        };
        
        set(state => ({
          evidence: { ...state.evidence, [id]: updated },
        }));
        
        // Sync to Supabase
        if (isSupabaseAvailable()) {
          const supabase = getSafeSupabaseClient();
          if (supabase) {
            supabase.from(TABLE_NAME).update(evidenceToDb(updated)).eq('id', id).then(({ error }) => {
              if (error) {
                console.error('[EvidenceStore] Update error:', error);
                dispatchStoreError('Evidence', 'update evidence', error.message);
                return;
              }
              recordItemFieldChange(updated.itemId, 'evidence', existing.source, updated.source);
              if (updates.attributeBindings !== undefined) void saveBindings(id, updated.attributeBindings);
            });
          }
        }
      },
      
      // Delete evidence
      deleteEvidence: (id: string) => {
        const removed = get().evidence[id];
        set(state => {
          const { [id]: _removed, ...rest } = state.evidence;
          
          // Also remove bindings for this evidence
          const updatedBindings = { ...state.bindings };
          for (const [bindingId, binding] of Object.entries(updatedBindings)) {
            if (binding.evidenceId === id) {
              delete updatedBindings[bindingId];
            }
          }
          
          return {
            evidence: rest,
            bindings: updatedBindings,
          };
        });
        
        // Sync to Supabase
        if (isSupabaseAvailable()) {
          const supabase = getSafeSupabaseClient();
          if (supabase) {
            supabase.from(TABLE_NAME).delete().eq('id', id).then(({ error }) => {
              if (error) {
                console.error('[EvidenceStore] Delete error:', error);
                dispatchStoreError('Evidence', 'delete evidence', error.message);
                return;
              }
              if (removed) recordItemFieldChange(removed.itemId, 'evidence', removed.source, null);
            });
          }
        }
      },
      
      // Bind evidence to attribute
      bindToAttribute: (evidenceId: string, attributeKey: string, valueSupported: unknown, strength: 'strong' | 'moderate' | 'suggestive'): AttributeEvidenceBinding => {
        const id = uuidv4();
        const now = new Date();
        
        const binding: AttributeEvidenceBinding = {
          id,
          evidenceId,
          attributeKey,
          valueSupported,
          strength,
          createdAt: now,
          createdBy: useUserStore.getState().userId ?? '',
        };
        
        set(state => {
          // Update bindings
          const updatedBindings = { ...state.bindings, [id]: binding };
          
          // Update evidence with binding reference
          const evidence = state.evidence[evidenceId];
          if (evidence) {
            const updatedEvidence = {
              ...evidence,
              attributeBindings: [...evidence.attributeBindings, binding],
            };
            return {
              bindings: updatedBindings,
              evidence: { ...state.evidence, [evidenceId]: updatedEvidence },
            };
          }
          
          return { bindings: updatedBindings };
        });
        
        // Saved, not just held on screen.
        const supabase = isSupabaseAvailable() ? getSafeSupabaseClient() : null;
        if (supabase) {
          void supabase.from(BINDINGS_TABLE).insert(bindingToDb(binding)).then(({ error }) => {
            if (error) dispatchStoreError('Evidence', 'link it to a value', error.message);
          });
        }
        return binding;
      },
      
      // Unbind evidence from attribute
      unbindFromAttribute: (bindingId: string) => {
        const supabase = isSupabaseAvailable() ? getSafeSupabaseClient() : null;
        if (supabase) {
          void supabase.from(BINDINGS_TABLE).delete().eq('id', bindingId).then(({ error }) => {
            if (error) dispatchStoreError('Evidence', 'unlink it from a value', error.message);
          });
        }
        set(state => {
          const binding = state.bindings[bindingId];
          if (!binding) return state;
          
          const { [bindingId]: _removed, ...restBindings } = state.bindings;
          
          // Update evidence
          const evidence = state.evidence[binding.evidenceId];
          if (evidence) {
            const updatedEvidence = {
              ...evidence,
              attributeBindings: evidence.attributeBindings.filter(b => b.id !== bindingId),
            };
            return {
              bindings: restBindings,
              evidence: { ...state.evidence, [binding.evidenceId]: updatedEvidence },
            };
          }
          
          return { bindings: restBindings };
        });
      },
      
      // Verify evidence
      verifyEvidence: (id: string, verifierId: string, note?: string) => {
        set(state => {
          const existing = state.evidence[id];
          if (!existing) return state;
          
          const updated: Evidence = {
            ...existing,
            verifiedBy: verifierId,
            verifiedAt: new Date(),
            verificationNote: note,
          };
          
          return {
            evidence: { ...state.evidence, [id]: updated },
          };
        });
      },
      
      // Bulk delete
      bulkDeleteEvidence: (ids: string[]) => {
        set(state => {
          const updated = { ...state.evidence };
          const updatedBindings = { ...state.bindings };
          
          for (const id of ids) {
            delete updated[id];
            
            // Remove bindings
            for (const [bindingId, binding] of Object.entries(updatedBindings)) {
              if (binding.evidenceId === id) {
                delete updatedBindings[bindingId];
              }
            }
          }
          
          return { evidence: updated, bindings: updatedBindings };
        });
      },
      
      // Delete all evidence for an item
      deleteEvidenceForItem: (itemId: string) => {
        const evidenceForItem = get().getEvidenceForItem(itemId);
        get().bulkDeleteEvidence(evidenceForItem.map(e => e.id));
      },
      
      // Get evidence by ID
      getEvidenceById: (id: string): Evidence | undefined => {
        return get().evidence[id];
      },
      
      // Get evidence for item
      getEvidenceForItem: (itemId: string): Evidence[] => {
        return Object.values(get().evidence).filter(e => e.itemId === itemId);
      },
      
      // Get evidence for attribute
      getEvidenceForAttribute: (itemId: string, attributeKey: string): Evidence[] => {
        return Object.values(get().evidence).filter(e => 
          e.itemId === itemId && 
          e.attributeBindings.some(b => b.attributeKey === attributeKey)
        );
      },
      
      // Get evidence quality metrics for item
      getEvidenceQuality: (itemId: string): EvidenceQualityMetrics => {
        const evidence = get().getEvidenceForItem(itemId);
        
        // Count by type
        const byType: Record<EvidenceType, number> = {
          data: 0,
          research: 0,
          expert_opinion: 0,
          customer_feedback: 0,
          market_signal: 0,
          competitor_intel: 0,
          internal_observation: 0,
          assumption: 0,
        };
        
        // Count by reliability
        const byReliability: Record<EvidenceReliability, number> = {
          verified: 0,
          corroborated: 0,
          single_source: 0,
          anecdotal: 0,
          speculative: 0,
        };
        
        // Track attributes
        const attributesWithEvidence = new Set<string>();
        
        for (const e of evidence) {
          byType[e.type]++;
          byReliability[e.reliability]++;
          
          for (const binding of e.attributeBindings ?? []) {
            attributesWithEvidence.add(binding.attributeKey);
          }
        }
        
        return {
          itemId,
          totalEvidence: evidence.length,
          byType,
          byReliability,
          hasVerifiedEvidence: evidence.some(e => !!e.verifiedBy),
          onlyAssumptions: evidence.length > 0 && evidence.every(e => e.type === 'assumption'),
          hasExpiredEvidence: evidence.some(e => e.isExpired),
          attributesWithEvidence: Array.from(attributesWithEvidence),
          attributesWithoutEvidence: [], // Would need attribute schema to compute
          qualityScore: calculateEvidenceQualityScore(evidence),
          computedAt: new Date(),
        };
      },
      
      // Get evidence by filters
      getEvidenceByFilters: (filters: EvidenceFilters): Evidence[] => {
        let results = Object.values(get().evidence);
        
        if (filters.itemId) {
          results = results.filter(e => e.itemId === filters.itemId);
        }
        
        if (filters.types && filters.types.length > 0) {
          results = results.filter(e => filters.types!.includes(e.type));
        }
        
        if (filters.reliabilities && filters.reliabilities.length > 0) {
          results = results.filter(e => filters.reliabilities!.includes(e.reliability));
        }
        
        if (filters.isVerified !== undefined) {
          results = results.filter(e => filters.isVerified ? !!e.verifiedBy : !e.verifiedBy);
        }
        
        if (filters.isExpired !== undefined) {
          results = results.filter(e => e.isExpired === filters.isExpired);
        }
        
        if (filters.addedBy) {
          results = results.filter(e => e.addedBy === filters.addedBy);
        }
        
        if (filters.attributeKey) {
          results = results.filter(e => 
            e.attributeBindings.some(b => b.attributeKey === filters.attributeKey)
          );
        }
        
        if (filters.tags && filters.tags.length > 0) {
          results = results.filter(e => 
            e.tags && filters.tags!.some(tag => e.tags!.includes(tag))
          );
        }
        
        return results;
      },
      
      // Get evidence statistics
      getEvidenceStatistics: (): EvidenceStatistics => {
        const allEvidence = Object.values(get().evidence);
        
        // Get unique items
        const itemIds = new Set(allEvidence.map(e => e.itemId));
        const itemsWithEvidence = itemIds.size;
        
        // Count by type
        const byType: Record<EvidenceType, number> = {
          data: 0,
          research: 0,
          expert_opinion: 0,
          customer_feedback: 0,
          market_signal: 0,
          competitor_intel: 0,
          internal_observation: 0,
          assumption: 0,
        };
        
        // Count by reliability
        const byReliability: Record<EvidenceReliability, number> = {
          verified: 0,
          corroborated: 0,
          single_source: 0,
          anecdotal: 0,
          speculative: 0,
        };
        
        // Source counts
        const sourceCounts: Record<string, number> = {};
        
        // Recent count
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        let recentlyAdded = 0;
        
        // Verified count
        let verifiedCount = 0;
        
        for (const e of allEvidence) {
          byType[e.type]++;
          byReliability[e.reliability]++;
          
          sourceCounts[e.source] = (sourceCounts[e.source] || 0) + 1;
          
          if (e.addedAt >= sevenDaysAgo) recentlyAdded++;
          if (e.verifiedBy) verifiedCount++;
        }
        
        // Top sources
        const topSources = Object.entries(sourceCounts)
          .map(([source, count]) => ({ source, count }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 10);
        
        // Items with only assumptions
        let itemsWithOnlyAssumptions = 0;
        for (const itemId of itemIds) {
          const itemEvidence = allEvidence.filter(e => e.itemId === itemId);
          if (itemEvidence.every(e => e.type === 'assumption')) {
            itemsWithOnlyAssumptions++;
          }
        }
        
        return {
          totalEvidence: allEvidence.length,
          totalItems: 0, // Would need candidate store
          itemsWithEvidence,
          itemsWithoutEvidence: 0, // Would need candidate store
          itemsWithOnlyAssumptions,
          byType,
          byReliability,
          averageEvidencePerItem: itemsWithEvidence > 0 
            ? allEvidence.length / itemsWithEvidence 
            : 0,
          verifiedPercentage: allEvidence.length > 0 
            ? (verifiedCount / allEvidence.length) * 100 
            : 0,
          topSources,
          recentlyAdded,
        };
      },
      
      // Reset store
      reset: () => set(initialState),
      
      // Loading state
      setLoading: (loading: boolean) => set({ isLoading: loading }),
      setError: (error: string | null) => set({ error }),
    })
);

// Selector hooks — never return freshly allocated arrays/objects from the
// Zustand selector itself. Unstable snapshots trip React's useSyncExternalStore
// into "Maximum update depth exceeded" (React #185) as soon as the consumer mounts.
export const useEvidence = (id: string) =>
  useEvidenceStore((state) => state.evidence[id]);

export const useEvidenceForItem = (itemId: string) =>
  useEvidenceStore(
    useShallow((state) => Object.values(state.evidence).filter((e) => e.itemId === itemId)),
  );

export const useEvidenceQuality = (itemId: string) => {
  // Depend on the item's evidence list (shallow-stable), then recompute metrics.
  const evidence = useEvidenceForItem(itemId);
  return useMemo(() => {
    // `evidence` is the intentional invalidation signal; metrics are computed from store.
    return useEvidenceStore.getState().getEvidenceQuality(itemId);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- evidence identity is the cache key
  }, [evidence, itemId]);
};

export const useEvidenceStatistics = () => {
  // Recompute when the evidence map identity changes (writes replace the map).
  const evidence = useEvidenceStore((state) => state.evidence);
  return useMemo(() => {
    return useEvidenceStore.getState().getEvidenceStatistics();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- evidence map identity is the cache key
  }, [evidence]);
};
